import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { 
    FiPackage, FiPlus, FiTrash2, FiCheckCircle, 
    FiEye, FiSave, FiAlertCircle, FiFile, FiFileText,
    FiList, FiGrid, FiSearch, FiRefreshCw, FiBox,
    FiCalendar, FiPrinter, FiDownload
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PinModal from '../components/PinModal';

const Inventory = () => {
    const { user } = useAuth();
    const [inventories, setInventories] = useState([]);
    const [selectedInventory, setSelectedInventory] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [inventoryToDelete, setInventoryToDelete] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        shop_id: '',
        start_date: new Date(new Date().setDate(1)),
        end_date: new Date()
    });
    const [counts, setCounts] = useState({});
    const [shops, setShops] = useState([]);
    const [viewMode, setViewMode] = useState('list');
    const [periodStart, setPeriodStart] = useState(new Date(new Date().setDate(1)));
    const [periodEnd, setPeriodEnd] = useState(new Date());

    const isAdmin = user?.role === 'admin';
    const canManage = isAdmin || user?.role === 'caissier';

    useEffect(() => {
        if (canManage) {
            fetchInventories();
            fetchShops();
        }
    }, []);

    const fetchInventories = async () => {
        try {
            setLoading(true);
            const response = await api.get('/inventory');
            setInventories(response.data || []);
        } catch (error) {
            console.error('Erreur fetch inventories:', error);
            toast.error('Erreur lors du chargement des inventaires');
            setInventories([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchShops = async () => {
        try {
            const response = await api.get('/shops');
            setShops(response.data || []);
        } catch (error) {
            console.error('Erreur chargement boutiques:', error);
            setShops([]);
        }
    };

    const fetchInventoryDetails = async (id) => {
        try {
            const startDate = periodStart.toISOString().split('T')[0];
            const endDate = periodEnd.toISOString().split('T')[0];
            
            const response = await api.get(`/inventory/${id}?start_date=${startDate}&end_date=${endDate}`);
            setSelectedInventory(response.data);
            
            const initialCounts = {};
            (response.data.items || []).forEach(item => {
                initialCounts[item.id] = item.counted_quantity || item.expected_quantity || 0;
            });
            setCounts(initialCounts);
            setShowDetailModal(true);
        } catch (error) {
            toast.error('Erreur lors du chargement des détails');
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!formData.name) {
            toast.error('Le nom de l\'inventaire est requis');
            return;
        }
        try {
            const submitData = {
                name: formData.name,
                description: formData.description,
                shop_id: formData.shop_id,
                start_date: formData.start_date ? formData.start_date.toISOString().split('T')[0] : null,
                end_date: formData.end_date ? formData.end_date.toISOString().split('T')[0] : null
            };
            const response = await api.post('/inventory', submitData);
            toast.success(`Inventaire créé avec succès !`);
            setShowModal(false);
            setFormData({ 
                name: '', 
                description: '', 
                shop_id: '',
                start_date: new Date(new Date().setDate(1)),
                end_date: new Date()
            });
            fetchInventories();
        } catch (error) {
            console.error('Erreur création:', error);
            toast.error(error.response?.data?.message || 'Erreur lors de la création');
        }
    };

    const handleUpdateCount = (itemId, value) => {
        const quantity = parseInt(value) || 0;
        setCounts({ ...counts, [itemId]: quantity });
    };

    const handleSaveCount = async (itemId) => {
        const counted_quantity = counts[itemId];
        try {
            await api.put(`/inventory/${selectedInventory.id}/items/${itemId}`, { counted_quantity });
            toast.success('Comptage enregistré');
            fetchInventoryDetails(selectedInventory.id);
        } catch (error) {
            toast.error('Erreur lors de l\'enregistrement');
        }
    };

    const handleValidateInventory = async () => {
        const hasChanges = selectedInventory.items?.some(item => 
            counts[item.id] !== undefined && counts[item.id] !== item.expected_quantity
        );
        
        if (!hasChanges) {
            if (!window.confirm('Aucun produit n\'a été modifié. Voulez-vous quand même valider ?')) {
                return;
            }
        } else {
            if (!window.confirm('Valider cet inventaire va mettre à jour les stocks. Continuer ?')) {
                return;
            }
        }
        
        setExporting(true);
        try {
            const response = await api.put(`/inventory/${selectedInventory.id}/validate`);
            toast.success(response.data.message || 'Inventaire validé avec succès');
            setShowDetailModal(false);
            fetchInventories();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erreur lors de la validation');
        } finally {
            setExporting(false);
        }
    };

    // ========== SUPPRESSION DE L'INVENTAIRE ==========
    const handleDeleteRequest = (inventory) => {
        setInventoryToDelete(inventory);
        setShowPinModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!inventoryToDelete) return;
        try {
            await api.delete(`/inventory/${inventoryToDelete.id}`);
            toast.success('Inventaire supprimé avec succès');
            setShowPinModal(false);
            setInventoryToDelete(null);
            fetchInventories();
            if (selectedInventory && selectedInventory.id === inventoryToDelete.id) {
                setShowDetailModal(false);
                setSelectedInventory(null);
            }
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    // ========== EXPORT PDF DE L'INVENTAIRE ==========
    const exportToPDF = async (inventory) => {
        setExporting(true);
        try {
            const startDate = periodStart.toISOString().split('T')[0];
            const endDate = periodEnd.toISOString().split('T')[0];
            
            const response = await api.get(`/inventory/${inventory.id}/export/pdf?start_date=${startDate}&end_date=${endDate}`);
            const data = response.data;
            
            const doc = new jsPDF({ unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            
            // En-tête
            doc.setFillColor(16, 185, 129);
            doc.rect(0, 0, pageWidth, 40, 'F');
            
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.text('MULTI SERVICE TECHNOLOGIES', pageWidth / 2, 20, { align: 'center' });
            
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text('Rapport d\'inventaire', pageWidth / 2, 32, { align: 'center' });
            doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, pageWidth / 2, 38, { align: 'center' });
            
            doc.setTextColor(0, 0, 0);
            
            // Informations
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(data.inventory.name, 14, 55);
            doc.setFont('helvetica', 'normal');
            doc.text(`Numéro: ${data.inventory.inventory_number}`, 14, 63);
            doc.text(`Statut: ${data.inventory.status === 'completed' ? 'Terminé' : data.inventory.status === 'in_progress' ? 'En cours' : 'Brouillon'}`, 14, 71);
            doc.text(`Période: ${data.summary.period.start_date} → ${data.summary.period.end_date}`, 14, 79);
            doc.text(`Créé par: ${data.inventory.created_by_name || '-'}`, 14, 87);
            
            // Résumé
            doc.setFillColor(245, 245, 245);
            doc.rect(14, 95, pageWidth - 28, 30, 'F');
            doc.setFont('helvetica', 'bold');
            doc.text('RÉSUMÉ', 20, 105);
            doc.setFont('helvetica', 'normal');
            doc.text(`Stock initial total: ${data.summary.total_expected}`, 20, 115);
            doc.text(`Ventes totales: ${data.summary.total_sold}`, 20, 123);
            doc.text(`Stock final calculé: ${data.summary.total_final}`, 120, 115);
            doc.text(`Stock compté: ${data.summary.total_counted}`, 120, 123);
            
            // Tableau
            const tableHeaders = [['SKU', 'Produit', 'Stock initial', 'Ventes', 'Stock final', 'Stock compté', 'Écart']];
            const tableRows = data.items.slice(0, 50).map(item => {
                const stockFinal = (item.expected_quantity || 0) - (item.total_sold || 0);
                const difference = (item.counted_quantity || 0) - (item.expected_quantity || 0);
                return [
                    item.sku || '---',
                    item.product_name.length > 25 ? item.product_name.substring(0, 22) + '...' : item.product_name,
                    (item.expected_quantity || 0).toString(),
                    (item.total_sold || 0).toString(),
                    stockFinal.toString(),
                    (item.counted_quantity || 0).toString(),
                    difference.toString()
                ];
            });
            
            doc.autoTable({
                startY: 135,
                head: tableHeaders,
                body: tableRows,
                theme: 'striped',
                styles: { fontSize: 7, cellPadding: 2 },
                headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255], fontStyle: 'bold' },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 50 },
                    2: { cellWidth: 22, halign: 'center' },
                    3: { cellWidth: 20, halign: 'center' },
                    4: { cellWidth: 22, halign: 'center' },
                    5: { cellWidth: 22, halign: 'center' },
                    6: { cellWidth: 18, halign: 'center' }
                }
            });
            
            doc.save(`inventaire_${data.inventory.inventory_number}.pdf`);
            toast.success('Export PDF réussi');
        } catch (error) {
            console.error('Erreur export PDF:', error);
            toast.error('Erreur lors de l\'export PDF');
        } finally {
            setExporting(false);
        }
    };

    // ========== EXPORT EXCEL ==========
    const exportToExcel = async (inventory) => {
        setExporting(true);
        try {
            const startDate = periodStart.toISOString().split('T')[0];
            const endDate = periodEnd.toISOString().split('T')[0];
            
            const response = await api.get(`/inventory/${inventory.id}?start_date=${startDate}&end_date=${endDate}`);
            const data = response.data;
            
            const exportData = data.items.map(item => ({
                'SKU': item.sku || '---',
                'Produit': item.product_name,
                'Catégorie': item.category_name || '-',
                'Stock Initial': item.expected_quantity || 0,
                'Ventes': item.total_sold || 0,
                'Stock Final (calculé)': (item.expected_quantity || 0) - (item.total_sold || 0),
                'Stock compté': counts[item.id] || item.counted_quantity || 0,
                'Écart': (counts[item.id] || item.counted_quantity || 0) - (item.expected_quantity || 0),
                'Statut': item.status === 'counted' ? 'Compté' : 'En attente'
            }));
            
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Inventaire');
            
            const colWidths = [
                { wch: 20 }, { wch: 40 }, { wch: 20 }, 
                { wch: 15 }, { wch: 15 }, { wch: 18 },
                { wch: 15 }, { wch: 12 }, { wch: 15 }
            ];
            ws['!cols'] = colWidths;
            
            XLSX.writeFile(wb, `inventaire_${data.inventory.inventory_number}.xlsx`);
            toast.success('Export Excel réussi');
        } catch (error) {
            console.error('Erreur export Excel:', error);
            toast.error('Erreur lors de l\'export Excel');
        } finally {
            setExporting(false);
        }
    };

    const getStatusBadge = (status) => {
        const colors = {
            draft: 'bg-gray-100 text-gray-800',
            in_progress: 'bg-blue-100 text-blue-800',
            completed: 'bg-green-100 text-green-800',
            cancelled: 'bg-red-100 text-red-800'
        };
        const labels = {
            draft: 'Brouillon',
            in_progress: 'En cours',
            completed: 'Terminé',
            cancelled: 'Annulé'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
                {labels[status] || status}
            </span>
        );
    };

    if (!canManage) {
        return (
            <div className="text-center py-12">
                <FiAlertCircle className="mx-auto text-red-500" size={48} />
                <p className="text-gray-500 mt-2">Vous n'avez pas accès à cette page</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">📦 Inventaires</h1>
                    <p className="text-sm text-gray-500">Gérez et comptez vos stocks</p>
                </div>
                <div className="flex gap-3">
                    <div className="flex gap-2">
                        <button 
                            onClick={() => setViewMode('list')} 
                            className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-green-100 text-green-600' : 'bg-gray-100'}`}
                            title="Vue liste"
                        >
                            <FiList size={18} />
                        </button>
                        <button 
                            onClick={() => setViewMode('grid')} 
                            className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-green-100 text-green-600' : 'bg-gray-100'}`}
                            title="Vue grille"
                        >
                            <FiGrid size={18} />
                        </button>
                    </div>
                    <button
                        onClick={() => {
                            setFormData({ 
                                name: '', 
                                description: '', 
                                shop_id: '',
                                start_date: new Date(new Date().setDate(1)),
                                end_date: new Date()
                            });
                            setShowModal(true);
                        }}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                        <FiPlus size={18} />
                        Nouvel inventaire
                    </button>
                </div>
            </div>

            {/* Vue Liste des inventaires */}
            {viewMode === 'list' && (
                <div className="card overflow-x-auto">
                    <table className="w-full">
                        <thead className="border-b">
                            <tr className="text-left">
                                <th className="pb-3">N° Inventaire</th>
                                <th className="pb-3">Nom</th>
                                <th className="pb-3">Statut</th>
                                <th className="pb-3 text-center">Produits</th>
                                <th className="pb-3">Période</th>
                                <th className="pb-3">Créé par</th>
                                <th className="pb-3 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {inventories.map((inventory) => (
                                <tr key={inventory.id} className="border-b hover:bg-gray-50 transition-colors">
                                    <td className="py-3 font-mono text-sm text-gray-500">{inventory.inventory_number}</td>
                                    <td className="py-3 font-medium">{inventory.name}</td>
                                    <td className="py-3">{getStatusBadge(inventory.status)}</td>
                                    <td className="py-3 text-center font-medium">{inventory.total_products || 0}</td>
                                    <td className="py-3 text-sm text-gray-500">
                                        {inventory.start_date ? new Date(inventory.start_date).toLocaleDateString('fr-FR') : '-'} 
                                        → 
                                        {inventory.end_date ? new Date(inventory.end_date).toLocaleDateString('fr-FR') : '-'}
                                    </td>
                                    <td className="py-3 text-sm text-gray-600">{inventory.created_by_name || '-'}</td>
                                    <td className="py-3 text-center">
                                        <div className="flex justify-center gap-2">
                                            <button
                                                onClick={() => fetchInventoryDetails(inventory.id)}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                                title="Voir"
                                            >
                                                <FiEye size={16} />
                                            </button>
                                            <button
                                                onClick={() => exportToPDF(inventory)}
                                                disabled={exporting}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                                title="Imprimer PDF"
                                            >
                                                <FiPrinter size={16} />
                                            </button>
                                            <button
                                                onClick={() => exportToExcel(inventory)}
                                                disabled={exporting}
                                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                                                title="Exporter Excel"
                                            >
                                                <FiFile size={16} />
                                            </button>
                                            {inventory.status !== 'completed' && (
                                                <button
                                                    onClick={() => handleDeleteRequest(inventory)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                                    title="Supprimer"
                                                >
                                                    <FiTrash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {inventories.length === 0 && (
                        <div className="text-center py-12">
                            <FiBox className="mx-auto text-gray-300" size={48} />
                            <p className="text-gray-500 mt-2">Aucun inventaire trouvé</p>
                            <button onClick={() => setShowModal(true)} className="mt-4 bg-green-600 text-white px-4 py-2 rounded-lg">Créer le premier inventaire</button>
                        </div>
                    )}
                </div>
            )}

            {/* Vue Grille */}
            {viewMode === 'grid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {inventories.map((inventory) => (
                        <div key={inventory.id} className="card hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <h3 className="font-semibold text-lg text-gray-800">{inventory.name}</h3>
                                    <p className="text-xs text-gray-400">{inventory.inventory_number}</p>
                                </div>
                                {getStatusBadge(inventory.status)}
                            </div>
                            <div className="border-t pt-3 mt-2">
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-500">Produits:</span>
                                    <span className="font-medium">{inventory.total_products || 0}</span>
                                </div>
                                <div className="flex justify-between text-sm mb-3">
                                    <span className="text-gray-500">Période:</span>
                                    <span className="font-medium">
                                        {inventory.start_date ? new Date(inventory.start_date).toLocaleDateString('fr-FR') : '-'}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button onClick={() => fetchInventoryDetails(inventory.id)} className="flex-1 bg-blue-600 text-white py-1.5 rounded-lg hover:bg-blue-700 text-sm flex items-center justify-center gap-1">
                                        <FiEye size={14} /> Voir
                                    </button>
                                    <button onClick={() => exportToPDF(inventory)} disabled={exporting} className="flex-1 bg-red-600 text-white py-1.5 rounded-lg hover:bg-red-700 text-sm flex items-center justify-center gap-1">
                                        <FiPrinter size={14} /> PDF
                                    </button>
                                    {inventory.status !== 'completed' && (
                                        <button onClick={() => handleDeleteRequest(inventory)} className="flex-1 bg-red-600 text-white py-1.5 rounded-lg hover:bg-red-700 text-sm flex items-center justify-center gap-1">
                                            <FiTrash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal création */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">📦 Nouvel inventaire</h2>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">✕</button>
                        </div>
                        <form onSubmit={handleCreate}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                                <input type="text" className="input-field" placeholder="Ex: Inventaire mensuel Juin 2026" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea className="input-field" rows="3" placeholder="Description de l'inventaire..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Boutique</label>
                                <select className="input-field" value={formData.shop_id} onChange={(e) => setFormData({ ...formData, shop_id: e.target.value })}>
                                    <option value="">Toutes les boutiques</option>
                                    {shops.map(shop => <option key={shop.id} value={shop.id}>{shop.name}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
                                    <DatePicker selected={formData.start_date} onChange={(date) => setFormData({ ...formData, start_date: date })} className="input-field" dateFormat="dd/MM/yyyy" maxDate={formData.end_date} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
                                    <DatePicker selected={formData.end_date} onChange={(date) => setFormData({ ...formData, end_date: date })} className="input-field" dateFormat="dd/MM/yyyy" minDate={formData.start_date} />
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button type="button" onClick={() => setShowModal(false)} className="flex-1 btn-secondary">Annuler</button>
                                <button type="submit" className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700">Créer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Détails */}
            {showDetailModal && selectedInventory && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
                    <div className="bg-white rounded-xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                            <div>
                                <h2 className="text-xl font-semibold">{selectedInventory.name}</h2>
                                <p className="text-sm text-gray-500">{selectedInventory.inventory_number}</p>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <button onClick={() => exportToPDF(selectedInventory)} disabled={exporting} className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm">
                                    <FiPrinter size={16} /> PDF
                                </button>
                                <button onClick={() => exportToExcel(selectedInventory)} disabled={exporting} className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm">
                                    <FiFile size={16} /> Excel
                                </button>
                                {selectedInventory.status !== 'completed' && (
                                    <>
                                        <button onClick={handleValidateInventory} disabled={exporting} className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm">
                                            <FiCheckCircle size={16} /> Valider
                                        </button>
                                        <button onClick={() => { setInventoryToDelete(selectedInventory); setShowPinModal(true); }} className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm">
                                            <FiTrash2 size={16} /> Supprimer
                                        </button>
                                    </>
                                )}
                                <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">✕</button>
                            </div>
                        </div>

                        {/* Résumé */}
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div><span className="text-sm text-gray-500">Statut</span><div className="mt-1">{getStatusBadge(selectedInventory.status)}</div></div>
                            <div><span className="text-sm text-gray-500">Créé le</span><p className="font-medium">{new Date(selectedInventory.created_at).toLocaleDateString('fr-FR')}</p></div>
                            <div><span className="text-sm text-gray-500">Créé par</span><p className="font-medium">{selectedInventory.created_by_name || '-'}</p></div>
                            <div><span className="text-sm text-gray-500">Produits</span><p className="font-medium">{selectedInventory.items?.length || 0}</p></div>
                            <div><span className="text-sm text-gray-500">Ventes période</span><p className="font-medium text-blue-600">{selectedInventory.items?.reduce((sum, item) => sum + (item.total_sold || 0), 0) || 0}</p></div>
                        </div>

                        {/* Tableau */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="border-b bg-gray-50">
                                    <tr className="text-left">
                                        <th className="pb-3 px-2">SKU</th>
                                        <th className="pb-3 px-2">Produit</th>
                                        <th className="pb-3 px-2 text-center">Stock Initial</th>
                                        <th className="pb-3 px-2 text-center">📊 Ventes</th>
                                        <th className="pb-3 px-2 text-center">Stock Final</th>
                                        <th className="pb-3 px-2 text-center">📝 Stock compté</th>
                                        <th className="pb-3 px-2 text-center">Écart</th>
                                        <th className="pb-3 px-2 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedInventory.items?.map((item) => {
                                        const stockInitial = item.expected_quantity || 0;
                                        const ventes = item.total_sold || 0;
                                        const stockFinalCalcule = stockInitial - ventes;
                                        const newStock = counts[item.id] !== undefined ? counts[item.id] : stockInitial;
                                        const difference = newStock - stockInitial;
                                        const isDifferent = difference !== 0;
                                        
                                        return (
                                            <tr key={item.id} className={`border-b hover:bg-gray-50 ${isDifferent ? 'bg-yellow-50' : ''}`}>
                                                <td className="py-2 px-2 font-mono text-sm">{item.sku || '---'}</td>
                                                <td className="py-2 px-2 font-medium">{item.product_name}</td>
                                                <td className="py-2 px-2 text-center font-medium">{stockInitial}</td>
                                                <td className="py-2 px-2 text-center font-medium text-blue-600">{ventes}</td>
                                                <td className="py-2 px-2 text-center font-medium text-purple-600">{stockFinalCalcule}</td>
                                                <td className="py-2 px-2 text-center">
                                                    <input type="number" className={`w-24 px-2 py-1 border rounded text-center ${isDifferent ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`} value={newStock} onChange={(e) => handleUpdateCount(item.id, e.target.value)} disabled={selectedInventory.status === 'completed'} min="0" />
                                                </td>
                                                <td className="py-2 px-2 text-center">
                                                    <span className={`font-medium ${difference > 0 ? 'text-green-600' : difference < 0 ? 'text-red-600' : 'text-gray-400'}`}>{difference > 0 ? `+${difference}` : difference}</span>
                                                </td>
                                                <td className="py-2 px-2 text-center">
                                                    {selectedInventory.status !== 'completed' && isDifferent && (
                                                        <button onClick={() => handleSaveCount(item.id)} className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 text-sm flex items-center gap-1 mx-auto">
                                                            <FiSave size={14} /> Enregistrer
                                                        </button>
                                                    )}
                                                    {selectedInventory.status !== 'completed' && !isDifferent && <span className="text-xs text-gray-400">Non modifié</span>}
                                                    {selectedInventory.status === 'completed' && isDifferent && <span className="text-xs text-orange-600 font-medium">✓ Ajusté</span>}
                                                </td>
                                             </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {selectedInventory.status !== 'completed' && (
                            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                                <p className="text-sm text-blue-700 flex items-center gap-2">
                                    <FiAlertCircle size={16} />
                                    💡 Colonnes : <strong>Stock Initial</strong> = Stock de départ | <strong>Ventes</strong> = Ventes sur la période | 
                                    <strong>Stock Final</strong> = Stock Initial - Ventes | <strong>Stock compté</strong> = Saisir le stock réel
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* PinModal pour la suppression */}
            <PinModal
                isOpen={showPinModal}
                onClose={() => {
                    setShowPinModal(false);
                    setInventoryToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
                title="Supprimer un inventaire"
                message="Êtes-vous sûr de vouloir supprimer cet inventaire ? Cette action est irréversible."
            />
        </div>
    );
};

export default Inventory;